import { LegSpeedRejection } from '@/services/PigRunSpeedRules';

export type TrackingWorksheetRowStatus =
  | 'passed'
  | 'next'
  | 'pending'
  | 'not-verified'
  | 'skipped';

export type TrackingWorksheetPassSource =
  | { kind: 'agm'; uid: number }
  | { kind: 'manual'; uid: null };

export type TrackingWorksheetDistanceSource = 'measured' | 'gps';

export const TrackingWorksheetColumnIds = {
  marker: 'marker',
  linkedAgms: 'linkedAgms',
  passSource: 'passSource',
  passUtc: 'passUtc',
  passLocal: 'passLocal',
  etaUtc: 'etaUtc',
  etaLocal: 'etaLocal',
  countdown: 'countdown',
  legSpeed: 'legSpeed',
  status: 'status',
  trackState: 'trackState',
  legDistance: 'legDistance',
  distanceFromLaunch: 'distanceFromLaunch',
  runAverageSpeed: 'runAverageSpeed',
  latitude: 'latitude',
  longitude: 'longitude',
} as const;

export type TrackingWorksheetColumnId =
  (typeof TrackingWorksheetColumnIds)[keyof typeof TrackingWorksheetColumnIds];

export type TrackingWorksheetTrackStateLabel = 'TRACK' | 'SKIP' | 'UNKNOWN';

export function formatTrackingWorksheetTrackState(
  trackState: string | null | undefined
): TrackingWorksheetTrackStateLabel {
  switch (trackState) {
    case 'T':
      return 'TRACK';
    case 'S':
      return 'SKIP';
    default:
      return 'UNKNOWN';
  }
}

export interface TrackingWorksheetRow {
  pipelineSectionMarkerId: number;
  sortOrder: number;
  markerName: string;
  description: string;
  trackState: string;
  status: TrackingWorksheetRowStatus;
  linkedUids: number[];
  passSource: TrackingWorksheetPassSource | null;
  passageTimeUtc: Date | null;
  etaUtc: Date | null;
  countdownSeconds: number | null;
  /**
   * True for the row the summary shows as Next: the 'next' Track row or, once
   * every Track marker is passed, the Receive row even when it is Skipped.
   */
  isSummaryNext: boolean;
  /**
   * When the pig should have reached this row, from verified data only: the
   * last passed marker's passage time (or the launch time) plus the leg
   * distances to here at the current speed. Set on the summary Next row only.
   */
  expectedArrivalUtc: Date | null;
  /**
   * Seconds the pig is past expectedArrivalUtc without a verified passage here
   * (0 when not yet due), or null when it cannot be computed.
   */
  overdueSeconds: number | null;
  legDistanceMeters: number | null;
  legDistanceSource: TrackingWorksheetDistanceSource | null;
  distanceFromLaunchMeters: number | null;
  legSpeedMetersPerSecond: number | null;
  /**
   * Why PigRunManager ignores this verified leg for the current speed (too
   * short, too fast), or null when it is used (EN-1495 review round 2, R2-7).
   */
  legSpeedRejection: LegSpeedRejection | null;
  runAverageSpeedMetersPerSecond: number | null;
  latitude: number | null;
  longitude: number | null;
}

export interface TrackingWorksheetRunSummary {
  pigRunId: number;
  runName: string;
  jobNumber: string | null;
  pipelineName: string | null;
  lifecycleState: string;
  launchTimeUtc: Date | null;
  receiveTimeUtc: Date | null;
  currentSpeedMetersPerSecond: number | null;
  verifiedMarkerCount: number;
  markerCount: number;
  deviceCount: number | null;
  passageCount: number | null;
  verifiedPassageCount: number | null;
  nextMarkerName: string | null;
  nextMarkerEtaUtc: Date | null;
  /** Same value as the Next row's overdueSeconds. */
  nextMarkerOverdueSeconds: number | null;
  endEtaUtc: Date | null;
  lastUpdatedUtc: Date | null;
}

export interface TrackingWorksheet {
  summary: TrackingWorksheetRunSummary;
  distanceFromLaunchSource: TrackingWorksheetDistanceSource;
  rows: TrackingWorksheetRow[];
  builtAtUtc: Date;
}
